import React, { useEffect, useState } from "react";
import {
  currentDateFormat,
  getLanguage,
  pageIsSearchable,
} from "../Managers/dataProvider";
import {
  BranchModel,
  EnumMenuType,
  EnumPopupFunctions,
  PopupInfoModel,
  ReportModel,
} from "../Managers/dataStructures";
import {
  dateToString,
  dateToStringForPicker,
  filterReportSearchData,
} from "../Managers/utilityFunctions";
import { ActivityIndicator } from "../Managers/utilityComponents";
import { fetchAdminReportsDaily } from "../Managers/wepApiManager";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";

interface ReportsProps {
  openReportDialog: (item: PopupInfoModel) => void;
  searchString: string;
  array_branches: BranchModel[];
}

export default function Reports(props: ReportsProps) {
  const isEnglish = getLanguage();
  const [array_reports, setReports] = useState<ReportModel[]>([]);
  const [activityIndicator, setActivityIndicator] = useState<boolean>(false);
  const [selectedDate, setSelectDate] = useState<Date>(new Date());

  const currentDate: string = dateToString(selectedDate);
  const currentDateForPicker: string = dateToStringForPicker(selectedDate);

  const searchString: string = pageIsSearchable(EnumMenuType.REPORTS)
    ? props.searchString
    : "";

  const array_filteredReports: ReportModel[] = filterReportSearchData(
    array_reports,
    searchString
  );

  useEffect(() => {
    updateReportList(selectedDate);
  }, [selectedDate]);

  function onReportDetail(report: ReportModel) {
    report.date = selectedDate;
    const item: PopupInfoModel = {
      item: report,
      props: report,
      ftype: EnumPopupFunctions.ADMIN_REPORT_DETAIL,
      callback: (report_: ReportModel) => {
        console.log("report approve and sent=" + report_.title);
      },
    };
    props.openReportDialog(item);
  }

  function onDateChange(x: dayjs.Dayjs | null) {
    //console.log("selected Date=" + x?.format("DD-MMM-YYYY"));
    setSelectDate(x?.toDate() as Date);
    updateReportList(x?.toDate() as Date);
  }

  function updateReportList(sDate: Date) {
    if (sDate !== undefined && sDate !== null) {
      setActivityIndicator(true);
      fetchAdminReportsDaily(sDate, (array_reports_: ReportModel[]) => {
        array_reports_.sort((a, b) =>
          (a.date !== undefined ? (a.date as Date) : 1) >
          (b.date !== undefined ? (b.date as Date) : 1)
            ? 1
            : -1
        );
        setReports(array_reports_);
        setActivityIndicator(false);
      });
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {isEnglish ? "Daily Reports" : "Tagesbericht Filiale"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isEnglish
              ? "A list of Daily Reports along with their dates for Admin."
              : "Hier sehen Sie die täglichen Arbeitsberichte, sortiert nach Filialen."}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none"></div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="px-8 mt-4 sm:flex mr-5">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label={isEnglish ? "Select Date" : "Bitte Datum auswählen"}
                  //disableFuture
                  format={currentDateFormat()}
                  onAccept={(x) => onDateChange(x)}
                  defaultValue={dayjs(currentDateForPicker)}
                  disabled={false}
                  //sx={{ height: "5px", alignContent: "center", width: "20px" }}
                />
              </DemoContainer>
            </LocalizationProvider>
            <div className="sm:flex-auto sm:flex mr-2 "></div>
          </div>

          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="relative">
              {/* Selected row actions, only show when rows are selected. */}
              {/* <div className="absolute top-0 left-14 flex h-12 items-center space-x-3 bg-white sm:left-12"> */}
              {/*   <button type="button" className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">Bulk edit</button> */}
              {/*   <button type="button" className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">Delete all</button> */}
              {/* </div> */}
              {activityIndicator === true ? (
                <div className="mt-5 flex items-center justify-center">
                  {ActivityIndicator(1)}
                </div>
              ) : array_filteredReports.length === 0 ? (
                <div className="mt-10 flex justify-center font-semibold">
                  {selectedDate === undefined
                    ? isEnglish
                      ? "Please select date."
                      : "Bitte Datum auswählen"
                    : isEnglish
                    ? "No Report Available"
                    : "Keine Berichte verfügbar"}
                </div>
              ) : (
                <table className="mt-5 min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Branch" : "Filiale"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Shift" : "Schift"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Team Lead" : "Teamleiter"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Approved By" : "Freigegeben von"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {/* Selected: "bg-gray-50" */}
                    {array_filteredReports.map((x) => [
                      CellReport(x, onReportDetail, currentDate),
                    ])}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CellReport(
  report: ReportModel,
  onReportDetail: (report: ReportModel) => void,
  dateString: string
) {
  // function onDownload() {
  //   handler(report);
  // }
  const isEnglish = getLanguage();
  function onReportDetail_() {
    onReportDetail(report);
  }
  return (
    <tr key={report.report_id}>
      <td
        className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900"
        onClick={onReportDetail_}
      >
        {report.department}
      </td>
      <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
        onClick={onReportDetail_}
      >
        {report.shift_plan?.shift.title}
      </td>
      <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
        onClick={onReportDetail_}
      >
        {report.team_lead_info?.first_name +
          " " +
          report.team_lead_info?.last_name}
      </td>
      <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
        onClick={onReportDetail_}
      >
        {report.approved_by_info?.first_name +
          " " +
          report.approved_by_info?.last_name}
      </td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
        <a
          onClick={onReportDetail_}
          href={"#" + report.report_id}
          className="text-indigo-600 hover:text-indigo-900"
        >
          {isEnglish ? "Detail" : "Details"}
          <span className="sr-only">{", " + report.title}</span>
        </a>
      </td>
      {/* <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
        onClick={onReportDetail_}
      >
        {report.department}
      </td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
        <a
          onClick={onDownload}
          href={"#" + report.report_id}
          className="text-indigo-600 hover:text-indigo-900"
        >
          Download<span className="sr-only">{", " + report.title}</span>
        </a>
      </td> */}
    </tr>
  );
}
